import axios from 'axios';
import { ENDPOINTS } from '../constants/endpoints';
import {
  ApprovalBand, BankFileFieldInfo, BankFilePreview, BankFileTemplate, BlockedBill, PaymentRun, PaymentRunPage,
  PaymentRunSettings, PaymentRunStatus, Proposal, ProposeRequest, RunSaveRequest, SupplierBankAccount,
  SupplierPaymentProfile, TemplateDefaultsResult,
} from '../interfaces/procurement/paymentRun.interface';

export interface BankFileDownload { blob: Blob; fileName: string }

/** The file name the server put in Content-Disposition (plain or RFC 5987), else a fallback. */
const fileNameOf = (disposition: string | undefined, fallback: string): string => {
  if (!disposition) return fallback;
  const star = /filename\*=UTF-8''([^;]+)/i.exec(disposition);
  if (star) {
    try { return decodeURIComponent(star[1]); } catch { /* fall through */ }
  }
  const plain = /filename="?([^";]+)"?/i.exec(disposition);
  return plain ? plain[1] : fallback;
};

export const PaymentRunService = {
  propose: async (req: ProposeRequest): Promise<Proposal> =>
    (await axios.post(ENDPOINTS.PAYMENT_RUN.PROPOSE, req)).data,
  create: async (req: RunSaveRequest): Promise<PaymentRun> =>
    (await axios.post(ENDPOINTS.PAYMENT_RUN.CREATE, req)).data,
  update: async (id: string, req: RunSaveRequest): Promise<PaymentRun> =>
    (await axios.put(ENDPOINTS.PAYMENT_RUN.BY_ID(id), req)).data,
  get: async (id: string): Promise<PaymentRun> => (await axios.get(ENDPOINTS.PAYMENT_RUN.BY_ID(id))).data,
  list: async (institutionId: string, status?: PaymentRunStatus, page = 0, size = 20): Promise<PaymentRunPage> =>
    (await axios.get(ENDPOINTS.PAYMENT_RUN.LIST(institutionId, status, page, size))).data,
  submit: async (id: string): Promise<PaymentRun> => (await axios.post(ENDPOINTS.PAYMENT_RUN.SUBMIT(id))).data,
  approve: async (id: string, comment?: string): Promise<PaymentRun> =>
    (await axios.post(ENDPOINTS.PAYMENT_RUN.APPROVE(id), { comment })).data,
  reject: async (id: string, reason: string): Promise<PaymentRun> =>
    (await axios.post(ENDPOINTS.PAYMENT_RUN.REJECT(id), { reason })).data,
  revise: async (id: string): Promise<PaymentRun> => (await axios.post(ENDPOINTS.PAYMENT_RUN.REVISE(id))).data,
  cancel: async (id: string): Promise<PaymentRun> => (await axios.post(ENDPOINTS.PAYMENT_RUN.CANCEL(id))).data,
  execute: async (id: string): Promise<PaymentRun> => (await axios.post(ENDPOINTS.PAYMENT_RUN.EXECUTE(id))).data,
  reverse: async (id: string): Promise<PaymentRun> => (await axios.post(ENDPOINTS.PAYMENT_RUN.REVERSE(id))).data,
  reverseLine: async (lineId: string): Promise<PaymentRun> =>
    (await axios.post(ENDPOINTS.PAYMENT_RUN.REVERSE_LINE(lineId))).data,
  bankFilePreview: async (id: string, templateId?: string): Promise<BankFilePreview> =>
    (await axios.get(ENDPOINTS.PAYMENT_RUN.BANK_FILE_PREVIEW(id, templateId))).data,
  bankFile: async (id: string, templateId?: string): Promise<BankFileDownload> => {
    const r = await axios.post(ENDPOINTS.PAYMENT_RUN.BANK_FILE(id, templateId), null, { responseType: 'blob' });
    return { blob: r.data, fileName: fileNameOf(r.headers?.['content-disposition'], 'bank-file.csv') };
  },
  advicePdf: async (id: string, supplierId: string): Promise<Blob> =>
    (await axios.get(ENDPOINTS.PAYMENT_RUN.ADVICE_PDF(id, supplierId), { responseType: 'blob' })).data,
  emailAdvice: async (id: string, supplierId: string): Promise<void> => {
    await axios.post(ENDPOINTS.PAYMENT_RUN.ADVICE_EMAIL(id, supplierId));
  },
  getSettings: async (institutionId: string): Promise<PaymentRunSettings> =>
    (await axios.get(ENDPOINTS.PAYMENT_RUN.SETTINGS(institutionId))).data,
  saveSettings: async (institutionId: string, discountReceivedAccountId: string | null): Promise<PaymentRunSettings> =>
    (await axios.put(ENDPOINTS.PAYMENT_RUN.SETTINGS(institutionId), { discountReceivedAccountId })).data,
  blockedBills: async (institutionId: string): Promise<BlockedBill[]> =>
    (await axios.get(ENDPOINTS.PAYMENT_RUN.BLOCKED_BILLS(institutionId))).data,
};

export const SupplierBankAccountService = {
  profile: async (supplierId: string): Promise<SupplierPaymentProfile> =>
    (await axios.get(ENDPOINTS.SUPPLIER_BANK_ACCOUNT.PROFILE(supplierId))).data,
  create: async (dto: SupplierBankAccount): Promise<SupplierBankAccount> =>
    (await axios.post(ENDPOINTS.SUPPLIER_BANK_ACCOUNT.CREATE, dto)).data,
  update: async (id: string, dto: SupplierBankAccount): Promise<SupplierBankAccount> =>
    (await axios.put(ENDPOINTS.SUPPLIER_BANK_ACCOUNT.BY_ID(id), dto)).data,
  makeDefault: async (id: string): Promise<SupplierBankAccount> =>
    (await axios.post(ENDPOINTS.SUPPLIER_BANK_ACCOUNT.MAKE_DEFAULT(id))).data,
  remove: async (id: string): Promise<void> => { await axios.delete(ENDPOINTS.SUPPLIER_BANK_ACCOUNT.BY_ID(id)); },
  saveTerms: async (supplierId: string, earlyDiscountPct: number | null, earlyDiscountDays: number | null):
    Promise<SupplierPaymentProfile> =>
    (await axios.put(ENDPOINTS.SUPPLIER_BANK_ACCOUNT.DISCOUNT_TERMS(supplierId), { earlyDiscountPct, earlyDiscountDays })).data,
};

export const PaymentApprovalBandService = {
  list: async (institutionId: string): Promise<ApprovalBand[]> =>
    (await axios.get(ENDPOINTS.PAYMENT_APPROVAL_BAND.LIST(institutionId))).data,
  create: async (dto: ApprovalBand): Promise<ApprovalBand> =>
    (await axios.post(ENDPOINTS.PAYMENT_APPROVAL_BAND.CREATE, dto)).data,
  update: async (id: string, dto: ApprovalBand): Promise<ApprovalBand> =>
    (await axios.put(ENDPOINTS.PAYMENT_APPROVAL_BAND.BY_ID(id), dto)).data,
  remove: async (id: string): Promise<void> => { await axios.delete(ENDPOINTS.PAYMENT_APPROVAL_BAND.BY_ID(id)); },
  /** Replaces the whole band set; the server validates the set together (no gaps or overlaps). */
  replaceAll: async (institutionId: string, dtos: ApprovalBand[]): Promise<ApprovalBand[]> =>
    (await axios.put(ENDPOINTS.PAYMENT_APPROVAL_BAND.LIST(institutionId), dtos)).data,
};

export const BankFileTemplateService = {
  list: async (institutionId: string): Promise<BankFileTemplate[]> =>
    (await axios.get(ENDPOINTS.BANK_FILE_TEMPLATE.LIST(institutionId))).data,
  fields: async (): Promise<BankFileFieldInfo[]> => (await axios.get(ENDPOINTS.BANK_FILE_TEMPLATE.FIELDS)).data,
  create: async (dto: BankFileTemplate): Promise<BankFileTemplate> =>
    (await axios.post(ENDPOINTS.BANK_FILE_TEMPLATE.CREATE, dto)).data,
  update: async (id: string, dto: BankFileTemplate): Promise<BankFileTemplate> =>
    (await axios.put(ENDPOINTS.BANK_FILE_TEMPLATE.BY_ID(id), dto)).data,
  remove: async (id: string): Promise<void> => { await axios.delete(ENDPOINTS.BANK_FILE_TEMPLATE.BY_ID(id)); },
  loadDefaults: async (institutionId: string): Promise<TemplateDefaultsResult> =>
    (await axios.post(ENDPOINTS.BANK_FILE_TEMPLATE.LOAD_DEFAULTS(institutionId))).data,
};
